import { useEffect, useRef } from 'react';

interface UseSocketRoomOptions<TArgs extends unknown[] = unknown[]> {
	socket:
		| {
				connected?: boolean;
				on: (eventName: string, handler: (...args: TArgs) => void) => void;
				off: (eventName: string, handler: (...args: TArgs) => void) => void;
		  }
		| null
		| undefined;
	enabled?: boolean;
	eventName: string;
	handler?: (...args: TArgs) => void;
	join?: () => void;
	leave?: () => void;
}

export function useSocketRoom<TArgs extends unknown[] = unknown[]>({
	socket,
	enabled = true,
	eventName,
	handler,
	join,
	leave,
}: UseSocketRoomOptions<TArgs>) {
	const handlerRef = useRef(handler);
	const joinRef = useRef(join);
	const leaveRef = useRef(leave);

	useEffect(() => {
		handlerRef.current = handler;
	}, [handler]);

	useEffect(() => {
		joinRef.current = join;
	}, [join]);

	useEffect(() => {
		leaveRef.current = leave;
	}, [leave]);

	useEffect(() => {
		if (!socket || !enabled || !eventName) {
			return;
		}

		const handleEvent = (...args: TArgs) => {
			handlerRef.current?.(...args);
		};

		const joinRoom = () => {
			joinRef.current?.();
		};

		if (socket.connected) {
			joinRoom();
		}

		socket.on('connect', joinRoom);
		socket.on(eventName, handleEvent);

		return () => {
			socket.off('connect', joinRoom);
			socket.off(eventName, handleEvent);
			leaveRef.current?.();
		};
	}, [socket, enabled, eventName]);
}
